import type { EChartsOption } from 'echarts';
import type { TooltipComponentFormatterCallbackParams } from 'echarts';
import type { ParsedTable, ChartGeneratorSettings } from '../types';
import {
  toNumber,
  axisPercentFormatter,
  resolveSeriesColors,
  GeneratorError,
  getChartColorByIndex,
  chartLegend,
  CHART_GRID_TOP_WITH_LEGEND,
  CHART_GRID_BOTTOM,
  uniqueCategoryNames,
  cleanCategoryName,
} from './helpers';
import { filterTableData } from '../utils/chartFilters';
import { sortTableForChart } from '../utils/chartSort';

/**
 * Generates ECharts options for a vertical Stacked Column chart
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Settings (selectedRows, selectedColumns, dataType)
 * @returns ECharts option configuration object for a stacked column chart
 * @throws {GeneratorError} If filtering removes all data
 */
export function stackedColumnOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {
  // Positions win over names (#1808); a set sort_order puts the bars in
  // reading order. Absent, table order as before.
  const sortOrder = settings?.sortOrder
  const pickedTable = filterTableData(
    t,
    settings?.selectedRows,
    settings?.selectedColumns,
    settings?.selectedRowPositions,
  )
  const filteredTable = sortOrder ? sortTableForChart(pickedTable, 'stackedColumn', sortOrder) : pickedTable

  const xAxisNames = filteredTable.headers.slice(1) // Column labels
  // Repeated labels stay separate columns (#1808); unique ones are unchanged.
  const xAxisData = uniqueCategoryNames(xAxisNames)
  const seriesNames = filteredTable.rows.map((row) => row[0] || 'Unnamed')

  const gradientMap = settings?.gradientMap

  const series: echarts.BarSeriesOption[] = filteredTable.rows.map((row, rowIndex) => {
    const data = xAxisData.map((_, colIndex) => {
      const dataIndex = colIndex + 1
      return dataIndex < row.length ? toNumber(row[dataIndex]) : null
    })

    // Look up gradient or color for this series based on series name
    const key = seriesNames[rowIndex].toLowerCase();
    const gradient = gradientMap?.[key];
    const color = settings?.colorMap?.[key] || getChartColorByIndex(rowIndex);

    return {
      name: seriesNames[rowIndex],
      type: 'bar',
      stack: 'total', // Enable stacking
      data: data,
      emphasis: {
        focus: 'series',
      },
      itemStyle: { color: gradient || color },
    }
  })

  const hasData = series.some((s) => s.data?.some((d) => d !== null && !isNaN(d as number)))
  if (!hasData) {
    throw new GeneratorError('No valid numeric data found after filtering.')
  }

  return {
    title: {
      text: title,
      left: 'left',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        if (!Array.isArray(params)) return ''
        let tooltipText = `${cleanCategoryName(params[0].name)}<br/>`
        params.forEach((item) => {
          const value = typeof item.value === 'number' ? item.value : null
          const formattedValue =
            value !== null
              ? settings?.dataType === 'freq'
                ? value.toLocaleString()
                : `${(value * 100).toFixed(1)}%`
              : 'N/A'
          const color = (item.color as string) || '#ccc'
          tooltipText += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>${item.seriesName}: ${formattedValue}<br/>`
        })
        return tooltipText
      },
    },
    legend: chartLegend(seriesNames),
    grid: {
      left: '3%',
      right: '4%',
      top: CHART_GRID_TOP_WITH_LEGEND,
      bottom: CHART_GRID_BOTTOM,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLabel: {
        // interval deliberately unset (#1178): ECharts' own 'auto' thins
        // colliding labels — 25 months no longer smear into one band. The
        // old force-every-label look is the "Every label" setting.
        rotate: 30,
        fontSize: 10,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: settings?.dataType === 'freq' ? undefined : axisPercentFormatter,
      },
    },
    series: series,
    color: resolveSeriesColors(seriesNames, settings?.colorMap),
  }
}
